import { Fragment } from 'react';
import type { AuditEvent } from '../../api/types';
import { useI18n } from '../../i18n/useI18n';
import { Drawer } from '../../ui/Drawer';
import { StatusBadge } from '../../ui/StatusBadge';
import { ActorMark } from './ActorMark';
import { actionWord, eventName } from './auditAction';
import { outcomeTone } from './auditOutcome';
import { detailFields, sessionOf, sessionTimeline } from './auditSession';
import { isRefusal, offsetFrom, saidBy } from './sessionLine';

/**
 * One row of the chain, whole, and the session it belongs to.
 *
 * The table shows five things because five is what a person scans; everything
 * else about a row is here — the class gdsgate filed it under, the detail it
 * carried, and the name a control plane asserted for whoever was behind it.
 *
 * **This is where the code goes**, and it goes only here. A code (`SES-0101`,
 * `POL-0201`) is not a word anybody scans a table by; it is what a row is
 * quoted by — into a reference book, a ticket, and one day a filter — and it is
 * the one part of a row that outlives the words beside it, because a code is
 * never reused and never renamed. A row is quoted from where it is read whole,
 * which is here, and a column of codes on a page of a hundred rows would be
 * paid for by one of the five that are scanned. Rows sealed before gdsgate
 * wrote codes carry none, and then there is no line: an absent code is shown
 * absent, not as a dash somebody would later have to be told is not a code.
 *
 * **The timeline is the reason this panel exists.** A session is a dozen rows
 * scattered through the chain — opened, allowed, bytes, closed — and reading it
 * out of a table means finding them by eye. Given one, they are gathered.
 */
export function AuditDrawer({
  event,
  events,
  onClose,
}: {
  event: AuditEvent;
  /** The page the chain was read as, which is as far as a timeline can reach. */
  events: readonly AuditEvent[];
  onClose: () => void;
}) {
  const { t, formatDateTime } = useI18n();
  const uid = sessionOf(event);
  const timeline = uid === undefined ? [] : sessionTimeline(events, uid);
  const sealedAs = eventName(event);
  const fields = detailFields(event.detail);

  // The word the row was clicked on, so the panel is about the row a person
  // pointed at rather than about a name they have not seen.
  return (
    <Drawer title={actionWord(event)} subtitle={event.target} onClose={onClose}>
      <dl className="dl">
        <dt>{t('audit.col.when')}</dt>
        <dd>{formatDateTime(new Date(event.at * 1000))}</dd>

        <dt>{t('audit.col.actor')}</dt>
        <dd>
          <ActorMark actor={event.actor} />
        </dd>

        {event.claimed_operator !== undefined && (
          <>
            <dt>{t('audit.claimed.label')}</dt>
            <dd className="mono" title={t('audit.claimed.why')}>
              {event.claimed_operator}
            </dd>
          </>
        )}

        <dt>{t('audit.col.class')}</dt>
        <dd className="mono">{event.class}</dd>

        {sealedAs !== undefined && (
          <>
            <dt>{t('audit.event.label')}</dt>
            <dd className="mono" title={t('audit.event.why')}>
              {sealedAs}
            </dd>
          </>
        )}

        {event.code !== undefined && (
          <>
            <dt>{t('audit.code.label')}</dt>
            <dd className="mono" title={t('audit.code.why')}>
              {event.code}
            </dd>
          </>
        )}

        <dt>{t('audit.col.outcome')}</dt>
        <dd>
          <StatusBadge tone={outcomeTone(event.outcome)} label={event.outcome} />
        </dd>

        {uid !== undefined && (
          <>
            <dt>{t('audit.session.label')}</dt>
            <dd className="mono">{uid}</dd>
          </>
        )}
      </dl>

      {/*
        The particulars, in whichever of the two forms the row is in — told
        apart by the row itself. A current cluster seals an object, and its
        fields are shown as fields, in the order it wrote them. Everything else
        is text and is shown as the text it is: the `key=value` of an older row,
        and the detail a server had to cut, which stopped being an object at the
        cut and would be a lie rendered as fields.
      */}
      <div style={{ marginTop: 18 }}>
        <div className="section-title">{t('audit.col.detail')}</div>
        {fields === undefined ? (
          <p className="mono" style={{ overflowWrap: 'anywhere' }}>
            {event.detail === '' ? '—' : event.detail}
          </p>
        ) : (
          <dl className="dl">
            {fields.map((field) => (
              <Fragment key={field.key}>
                <dt className="mono">{field.key}</dt>
                <dd className="mono" style={{ overflowWrap: 'anywhere' }}>
                  {field.value}
                </dd>
              </Fragment>
            ))}
          </dl>
        )}
        {event.detail_truncated && <span className="chip">{t('audit.truncated')}</span>}
      </div>

      {timeline.length > 1 && (
        <div style={{ marginTop: 22 }}>
          <div className="section-title">{t('audit.session.timeline')}</div>
          <ol className="timeline">
            {timeline.map((row) => (
              <li
                key={`${String(row.at)}-${row.action}-${row.detail}`}
                className={isRefusal(row) ? 'tl-item deny' : 'tl-item'}
              >
                <div className="tl-t">{offsetFrom(timeline[0]?.at ?? row.at, row.at)}</div>
                <div className="tl-b">
                  {/*
                    The same word the table printed: a rail of a session on a
                    current cluster is otherwise `policy.decision` at every
                    step where the session was allowed something.
                  */}
                  {isRefusal(row)
                    ? t('audit.session.denied', { reason: saidBy(row.detail) ?? actionWord(row) })
                    : actionWord(row)}
                  {!isRefusal(row) && saidBy(row.detail) !== undefined && (
                    <>
                      {' · '}
                      <span className="mono">{saidBy(row.detail)}</span>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ol>
          <p className="hint">{t('audit.session.window')}</p>
        </div>
      )}

    </Drawer>
  );
}
